import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { HumanDef, PackageManifest, QualityTier } from "./types";

export interface LoadedHuman {
  def: HumanDef;
  root: THREE.Object3D;
  mixer: THREE.AnimationMixer | null;
  actions: Map<string, THREE.AnimationAction>;
  defaultAction: THREE.AnimationAction | null;
  /** Compare-layer id when loaded via loadLayers. */
  layerId?: string;
  /** Seconds added to the shared clock for this instance. */
  timeOffset?: number;
}

export interface LoadResult {
  humans: LoadedHuman[];
  durationSeconds: number;
  /** Humans skipped because of tier maxHumans cap. */
  skippedHumans: number;
}

const loader = new GLTFLoader();
// Cookie-auth content URLs need credentials on cross-origin API hosts.
loader.setWithCredentials(true);

function pickGltfUrl(def: HumanDef, tier: QualityTier): string {
  const variant = def.qualityVariants?.[tier];
  return variant?.gltfUrl || def.gltfUrl;
}

/**
 * Load package humans into parent. Honors tier qualityVariants and maxHumans
 * budget (H7 group LOD — excess instances are not loaded).
 */
export async function loadPackage(
  manifest: PackageManifest,
  parent: THREE.Object3D,
  tier: QualityTier,
  maxHumans: number = 6,
  opts?: { clear?: boolean },
): Promise<LoadResult> {
  // Clear previous humans (skip when loading into a fresh compare-layer group).
  if (opts?.clear !== false) {
  while (parent.children.length > 0) {
    const child = parent.children[0];
    parent.remove(child);
    child.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else if (mat) mat.dispose();
    });
  }
  }

  const humans: LoadedHuman[] = [];
  let maxDuration = manifest.timeline?.durationSeconds ?? 0;
  const allDefs = manifest.humans ?? [];
  const cap = Math.max(0, Math.min(6, maxHumans));
  const defs = allDefs.slice(0, cap);
  const skippedHumans = Math.max(0, allDefs.length - defs.length);

  if (skippedHumans > 0) {
    console.info(
      `acroHumanScene: tier '${tier}' maxHumans=${cap}; skipped ${skippedHumans} instance(s)`,
    );
  }

  for (const def of defs) {
    const url = pickGltfUrl(def, tier);
    if (!url) continue;

    const gltf = await loader.loadAsync(url);

    // Wrap each instance so rootPosition / rootYawRad do not fight clip root tracks.
    const group = new THREE.Group();
    group.name = `human:${def.id}`;
    group.add(gltf.scene);
    if (def.rootPosition && def.rootPosition.length >= 3) {
      group.position.set(def.rootPosition[0], def.rootPosition[1], def.rootPosition[2]);
    }
    if (def.rootYawRad != null && Number.isFinite(def.rootYawRad)) {
      group.rotation.y = def.rootYawRad;
    }
    parent.add(group);

    let mixer: THREE.AnimationMixer | null = null;
    const actions = new Map<string, THREE.AnimationAction>();
    let defaultAction: THREE.AnimationAction | null = null;

    if (gltf.animations.length > 0) {
      mixer = new THREE.AnimationMixer(gltf.scene);
      for (const clip of gltf.animations) {
        const action = mixer.clipAction(clip);
        actions.set(clip.name, action);
        maxDuration = Math.max(maxDuration, clip.duration);
        if (!defaultAction) defaultAction = action;
      }
      if (def.defaultClip && actions.has(def.defaultClip)) {
        defaultAction = actions.get(def.defaultClip)!;
      }
      defaultAction?.reset().play();
    }

    humans.push({ def, root: group, mixer, actions, defaultAction });
  }

  return { humans, durationSeconds: maxDuration, skippedHumans };
}
